import React from 'react'

import compact from 'lodash/compact'

import { observer } from 'mobx-react'

import type { FilterOption } from 'modules/core/components/Filters/computeOptions'
import computeOptions from 'modules/core/components/Filters/computeOptions'
import type { FilterProps } from 'modules/core/components/Filters/types'
import SimpleUserSelector from 'modules/core/components/SimpleUserSelector'
import UserSelector from 'modules/core/components/UserSelector'
import { Fields } from 'modules/core/components/UserSelector/_filters/filterFields'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { employeeUser } from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import type { Employees } from 'modules/employees/store/collections/employees'
import employeesCollection from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'

const useUsers = createComputed((employees: Employees | Employee[]): User[] =>
  employees.map((e) => employeeUser(e))
)

const useSelectedIds = createComputed((selectedEmployeeIds: number[]): number[] =>
  compact(
    selectedEmployeeIds.map((id) => {
      const employee = employeesCollection.get(id)
      return employee && employee.attributes.access_id
    })
  )
)

const useOptions = (filterProps: FilterProps) => {
  const {
    hideFilterByStatus,
    hideFirstEmployeeFilter,
    showFilterByActive,
    showFilterByAway,
    showFilterByDirectManagedEmployee,
    showFilterByLegalEntity,
    showFilterByManagedEmployee,
    showFilterByTeam,
    showFilterByTimeoffSupervisor,
  } = filterProps

  const [computedOptions, setComputedOptions] = React.useState<Record<string, FilterOption[]>>({})

  const { loading } = useAsyncData(async () => {
    setComputedOptions(
      await computeOptions({
        hideFilterByStatus,
        hideFirstEmployeeFilter,
        showFilterByActive,
        showFilterByAway,
        showFilterByDirectManagedEmployee,
        showFilterByLegalEntity,
        showFilterByManagedEmployee,
        showFilterByTeam,
        showFilterByTimeoffSupervisor,
      })
    )
  }, [])

  return {
    computedOptions,
    loading,
  }
}

interface Props {
  employees: Employees | Employee[]
  showAll?: boolean
  onCleanFilter?: () => void
  onSelectEmployee?: (id?: number | null | undefined) => void
  onSelectMultiple?: (selectedEmployeeIds: number[]) => void
  selectedEmployeeIds: number[]
  filterProps?: FilterProps
  setDisplayedEmployees?: (employees: Employee[]) => void
  multiFilters?: boolean
  setChildPopoverOpen?: (childPopoverOpen: boolean) => void
  disabledEmployeeIds?: number[]
  getDisabledEmployeeReason?: (employee: Employee) => string | undefined
  showDefaultFilters?: boolean
  hideFields?: Fields[]
  filterKey?: string
  virtualized?: boolean
}

/**
 * @deprecated Use employees/components/EmployeeSelector instead
 */
const EmployeeSelector = ({
  employees,
  showAll = false, // when true, all `employees` will be displayed, `multiFilters` ignored
  onCleanFilter,
  onSelectEmployee,
  onSelectMultiple,
  selectedEmployeeIds,
  filterProps = {
    hideFilterByStatus: false,
    showFilterByTeam: true,
  },
  setDisplayedEmployees,
  multiFilters,
  setChildPopoverOpen,
  disabledEmployeeIds,
  getDisabledEmployeeReason,
  showDefaultFilters,
  hideFields,
  filterKey,
  virtualized = true,
}: Props) => {
  const users: User[] = useUsers(employees).get()

  const selectedIds: number[] = useSelectedIds(selectedEmployeeIds.slice()).get()

  const disabledUsers: number[] = useSelectedIds(disabledEmployeeIds?.slice() ?? []).get()
  const { computedOptions, loading } = useOptions(filterProps)

  const findEmployee = (accessId: number) => {
    const employee = employeesCollection.find({
      access_id: accessId,
    })

    if (!employee) {
      throw Error(`Error finding employees with access_id = ${accessId}`)
    }

    return employee
  }

  const onSelectMultipleUsers = (accessIds: number[]) => {
    if (!onSelectMultiple) return

    if (!accessIds.length) return onSelectMultiple([])

    const employees = accessIds.map(findEmployee)

    return onSelectMultiple(employees.map((e) => e.attributes.id))
  }

  const onSelect = (accessId: number | null | undefined) => {
    if (!onSelectEmployee) return

    if (!accessId) return onSelectEmployee()

    const employee = findEmployee(accessId)
    onSelectEmployee(employee.attributes.id)
  }

  const setDisplayedUsers = (accessIds: number[]) => {
    if (!setDisplayedEmployees) return

    if (!accessIds.length) return setDisplayedEmployees([])

    const employees = accessIds.map(findEmployee)

    return setDisplayedEmployees(employees)
  }

  const getDisabledUserReason = (accessId: number | null | undefined, disabled?: boolean) => {
    if (!disabled || !accessId || !getDisabledEmployeeReason) return

    const employee = findEmployee(accessId)
    const label = getDisabledEmployeeReason(employee)

    if (!label) return

    return {
      icon: 'info-circle-line',
      label,
    } as const
  }

  if (multiFilters) {
    return (
      <UserSelector
        onCleanFilter={onCleanFilter}
        onSelect={onSelect}
        onSelectMultiple={onSelectMultiple ? onSelectMultipleUsers : null}
        options={computedOptions}
        selectedIds={selectedIds}
        users={users}
        showAll={showAll}
        setDisplayedUsers={setDisplayedEmployees ? setDisplayedUsers : null}
        setChildPopoverOpen={setChildPopoverOpen}
        disabledUsers={disabledUsers}
        onGetUserExtraInformation={getDisabledUserReason}
        showDefaultFilters={showDefaultFilters}
        applyDefaultFilters={showDefaultFilters}
        hideFields={hideFields}
        filterKey={filterKey}
        virtualized={virtualized}
      />
    )
  }

  return (
    <SimpleUserSelector
      loadingOptions={loading}
      onCleanFilter={onCleanFilter}
      onSelect={onSelect}
      onSelectMultiple={onSelectMultiple ? onSelectMultipleUsers : null}
      options={computedOptions}
      selectedIds={selectedIds}
      users={users}
      showAll={showAll}
      setDisplayedUsers={setDisplayedEmployees ? setDisplayedUsers : null}
      setChildPopoverOpen={setChildPopoverOpen}
      disabledUsers={disabledUsers}
      onGetUserExtraInformation={getDisabledUserReason}
      virtualized={virtualized}
      defaultOption={filterProps.defaultOption}
    />
  )
}

/**
 * @deprecated Use employees/components/EmployeeSelector instead
 */
export default observer(EmployeeSelector)
